/**
 * 다양한 API 응답을 MinimalLessonEntity로 변환하는 유틸리티
 */

import { LessonBookingPolicy } from '@entities/lesson/lib/policies'
import { curriculumTypeSchema } from '@server/schemas'
import { getCourseDisplayTitle } from '@shared/utils'
import { z } from 'zod'

import type { MinimalLessonEntity, MinimalTicketEntity, MinimalUserEntity } from '../../model/types/view-model'
import { LessonViewModel } from '../view-models/card'

/**
 * 홈 API 응답 → MinimalLessonEntity
 */
export function mapNextLessonInfoToMinimal(nextLessonInfo: {
  class_id?: string | number | null
  prestudy_class_id?: string | number | null
  class_course_id?: string | number | null
  class_status?: string | null
  pre_study_time?: string | number | null
  teacher_id?: string | number | null
  teacher_name?: string | null
  class_level?: string | number | null
  class_level_name?: string | null
  class_week?: string | number | null
  lang_type?: string | null
  unix_class_start_datetime?: string | number | null
  unix_class_end_datetime?: string | number | null
  book_name?: string | null
  book_title?: string | Record<string, unknown> | null
  class_course_grade?: string | null
  class_count?: string | number | null
  comp_class_count?: string | number | null
  features?: string[]
}): MinimalLessonEntity {
  return {
    classStatus: nextLessonInfo.class_status ?? null,
    preStudyTime: Number(nextLessonInfo.pre_study_time ?? 0),
    classId: nextLessonInfo.class_id ? Number(nextLessonInfo.class_id) : null,
    prestudyClassId: nextLessonInfo.prestudy_class_id ? Number(nextLessonInfo.prestudy_class_id) : null,
    classCourseId: Number(nextLessonInfo.class_course_id ?? 0),
    teacherId: nextLessonInfo.teacher_id ? Number(nextLessonInfo.teacher_id) : null,
    classLevel: Number(nextLessonInfo.class_level ?? 1),
    classLevelName: getCourseDisplayTitle({
      classLevelName: nextLessonInfo.class_level_name,
      bookTitle: nextLessonInfo.book_title,
      bookName: nextLessonInfo.book_name,
      fallback: `Level ${Number(nextLessonInfo.class_level ?? 1)}`,
    }),
    classWeek: Number(nextLessonInfo.class_week ?? 1),
    langType: nextLessonInfo.lang_type ?? null,
    unixClassStartDatetime: nextLessonInfo.unix_class_start_datetime
      ? Number(nextLessonInfo.unix_class_start_datetime)
      : null,
    unixClassEndDatetime: nextLessonInfo.unix_class_end_datetime
      ? Number(nextLessonInfo.unix_class_end_datetime)
      : null,
    bookName:
      getCourseDisplayTitle({ bookTitle: nextLessonInfo.book_title, bookName: nextLessonInfo.book_name }) || null,
    classCourseGrade: nextLessonInfo.class_course_grade ?? null,
    teacherName: nextLessonInfo.teacher_name ?? null,
    classCount: Number(nextLessonInfo.class_count ?? 0),
    compClassCount: Number(nextLessonInfo.comp_class_count ?? 0),
    features: nextLessonInfo.features ?? [],
  }
}

/**
 * 커스텀 API 응답 → MinimalUserEntity
 */
export function mapUserInfoToMinimal(userInfo: {
  regClassCountForLangTypeList?: Array<{
    langType?: string | null
    curriculumType?: string
    regClassCount?: number
    isUnlimitedTicket?: 'Y' | 'N'
  }>
}): MinimalUserEntity {
  return {
    regClassCountForLangTypeList: (userInfo.regClassCountForLangTypeList ?? []).map((item) => ({
      langType: item.langType ?? null,
      curriculumType: item.curriculumType ?? 'PODO_BASIC',
      regClassCount: item.regClassCount ?? 0,
      isUnlimitedTicket: item.isUnlimitedTicket ?? 'N',
    })),
  }
}

/**
 * 티켓 리스트 → MinimalTicketEntity[]
 */
export function mapTicketsToMinimal(
  tickets: Array<{
    lang_type?: string | null
    remain_count_num?: number
    langType?: string | null
    remainCountNum?: number
  }>,
): MinimalTicketEntity[] {
  return tickets.map((ticket) => ({
    langType: ticket.langType ?? ticket.lang_type ?? null,
    remainCountNum: ticket.remainCountNum ?? ticket.remain_count_num ?? 0,
  }))
}

/**
 * 헬퍼: ViewModel 생성
 */
export function createLessonViewModel(
  lessonData: MinimalLessonEntity,
  userData: MinimalUserEntity,
  ticketData: MinimalTicketEntity[],
  curriculumType: z.infer<typeof curriculumTypeSchema>,
  timezone?: string,
): LessonViewModel {
  const lessonBookingPolicy = new LessonBookingPolicy(lessonData, userData, ticketData, curriculumType)

  return new LessonViewModel(lessonData, lessonBookingPolicy, timezone)
}
